import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import styled from '@emotion/styled'

import Link from 'components/common/A'
import NowrapDiv from 'components/common/NowrapDiv'
import TagButton, { OneLineGrid } from 'components/common/TagButton'
import { EyeSVG, PenSVG } from 'components/common/SVGElements'
import VenderLink from 'components/common/VenderLink'
import { setArticleClicked } from 'utils/api'

export const ArticleKeywordWrapper = styled(Box)`
  // 子元素保持原本的寬度，不被壓縮
  & > * {
    flex-shrink: 0;
  }
`

const ImgRect = styled.div`
  position: relative;
  padding-top: 75%;
  width: 120px;
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      width: 196px;
    }
    ${theme.breakpoints.up('xl')} {
      width: 240px;
    }
  `}
`
const Absolute = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`
const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  background: url('/img/img_headshot_default.png');
  background-size: 100%;
  background-position: center;
`

const ArticleSummary = styled(Typography)`
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  display: none;
  ${({ theme, $usedInVenderPage }) => `
    ${theme.breakpoints.up($usedInVenderPage ? 'xs' : 'md')} {
      display: -webkit-box;
      -webkit-line-clamp: 2;
    }
    ${theme.breakpoints.up('xl')} {
      -webkit-line-clamp: 3;
    }
  `}
`

const ArticleListItem = ({ article, usedInVenderPage }) => {
  const theme = useTheme()
  const isMedium = useMediaQuery(theme.breakpoints.down('md'))
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))

  const image = article?.mediumImage || article?.image
  const url = `/article/${article?.urlId}`
  const articleViews = article?.publicViews ?? 0

  const handleArticleClick = (event) => {
    if (usedInVenderPage || !article?._id) return

    // 避免把點擊作者/標籤誤算成點擊文章
    const clickedLink = event.target.closest?.('a')
    if (clickedLink && clickedLink !== event.currentTarget) return

    setArticleClicked(article._id)
  }

  return (
    <Link href={url} $origin target="_blank" onClick={handleArticleClick}>
      <Box display="flex">
        <Box>
          <ImgRect>
            <Absolute>
              <Img src={image?.url} />
            </Absolute>
          </ImgRect>
        </Box>
        <Box ml={{ xs: 4, xl: 5, xxl: 6 }} flex={1} minWidth={0}>
          <Typography
            sx={{
              typography: {
                xs: 'titleLarge',
                lg: 'headlineSmall',
                xxl: 'headlineMedium',
              },
            }}
            color="textPrimary"
          >
            <NowrapDiv $lines={2}>{article?.title}</NowrapDiv>
          </Typography>
          {!!article?.summary && (
            <ArticleSummary
              sx={{
                typography: { xs: 'paraBodySmall', xl: 'paraBodyMedium' },
              }}
              color="textSecondary"
              $usedInVenderPage={usedInVenderPage}
            >
              <Box mt={1}>{article?.summary}</Box>
            </ArticleSummary>
          )}
          {/* 若在廠商頁面且 lg 以下則完全不顯示 */}
          {!(usedInVenderPage && !isLarge) && (
            <OneLineGrid
              container
              spacing={4}
              ml={0}
              $height={isMedium ? 'auto' : '35px'}
              $width="100%"
              mt={3}
              flexWrap="nowrap"
            >
              {/* 若在廠商頁面則不顯示文章作者與閱覽數 */}
              {!usedInVenderPage && (
                <Box display="inline-flex" alignItems="center" mb={1}>
                  {/* 文章作者 */}
                  <Box
                    mr={4}
                    display="flex"
                    flexWrap="nowrap"
                    alignItems="center"
                  >
                    {article?.vender?.introductionInfo?.isVisible ? (
                      <VenderLink
                        vender={article?.vender}
                        width={isMedium ? '16px' : '20px'}
                        height={isMedium ? '16px' : '20px'}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" flexWrap="nowrap">
                        <PenSVG
                          width={isMedium ? '16px' : '20px'}
                          height={isMedium ? '16px' : '20px'}
                          $color="text.disabled"
                        />
                        <Typography
                          ml={1.5}
                          color="text.disabled"
                          sx={{
                            typography: { xs: 'bodyXs', md: 'bodySmall' },
                          }}
                        >
                          <NowrapDiv>{article?.byline}</NowrapDiv>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {/* 閱覽數 */}
                  <Box
                    mr={4}
                    display="flex"
                    alignItems="center"
                    flexWrap="nowrap"
                  >
                    <EyeSVG
                      width={isMedium ? '16px' : '20px'}
                      height={isMedium ? '16px' : '20px'}
                      $color="text.disabled"
                    />
                    <Typography
                      ml={1.5}
                      color="text.disabled"
                      sx={{
                        typography: { xs: 'bodyXs', md: 'bodySmall' },
                      }}
                    >
                      {articleViews}
                    </Typography>
                  </Box>
                </Box>
              )}
              {/* 關鍵字標籤區 */}
              {!!article?.keywords?.length && isLarge && (
                <ArticleKeywordWrapper>
                  {article.keywords.map((keyword, index) => (
                    <Box display="inline-block" mr={2} mb={1} key={index}>
                      <TagButton
                        href={`/article/tag/${keyword.urlId}`}
                        size="sm"
                      >
                        <NowrapDiv>{keyword.name}</NowrapDiv>
                      </TagButton>
                    </Box>
                  ))}
                </ArticleKeywordWrapper>
              )}
            </OneLineGrid>
          )}
        </Box>
      </Box>
    </Link>
  )
}

export default ArticleListItem
